/**
 * A FOTÓZÁSI MUNKAMENET — a lapok, amíg a kamera nyitva van.
 *
 * ⚠️ EZ MÉG NEM AZ IndexedDB-S MEGŐRZÉS (Sz6). Ez a memóriabeli állapot: a
 * sorozat, amíg a képernyő él. A 24 órás megőrzés külön lépés, és pont azért
 * külön, mert MÁS a bukásmódja — ott az iOS ITT-je és a tárhely-ürítés a
 * kérdés, itt a `URL.revokeObjectURL`.
 *
 * ── AMIÉRT SAJÁT HOOK, ÉS NEM `useState` A KOMPONENSBEN ─────────────────
 *
 * Az objektum-URL-ek miatt. Minden bélyegkép egy `URL.createObjectURL`, ami
 * addig tartja a `Blob`-ot a memóriában, amíg valaki vissza nem vonja. Egy
 * húszoldalas sorozat visszavonás nélkül több száz megabájt — és a
 * `szkenneles-terv.md` §6 szerint az iOS Safari lapja ennél kevesebbtől is
 * elszáll. A visszavonásnak EGY helyen kell lennie, különben a következő
 * törlés-gomb megírásakor kimarad.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CapturedPage, QuadCorners } from './document-scanner';

export interface SessionPage {
  /** Stabil azonosító — a React `key`-je és a törlés fogódzója. */
  id: string;
  blob: Blob;
  /** Bélyegkép-URL. A hook vonja vissza, senki más. */
  previewUrl: string;
  /** Hamis, ha a lapélt nem találtuk meg — az áttekintőn ez látszik. */
  cropped: boolean;
  /** Az eredeti fotó a vágáshoz (#89) — lásd `CapturedPage.original`. */
  original?: Blob;
  corners?: QuadCorners | null;
}

export interface CaptureSession {
  pages: SessionPage[];
  add: (page: CapturedPage) => void;
  remove: (id: string) => void;
  /**
   * EGY LAP ÚJRAFOTÓZÁSA — a HELYÉN marad.
   *
   * Nem `remove` + `add`: az a lapot a sorozat VÉGÉRE tenné. Aki egy
   * háromoldalas számla második oldalát fotózza újra, nem sorrendet akar
   * változtatni — és egy némán hátracsúszó oldal pont az a hiba, amit a
   * `@@unique([documentId, pageNumber])` a szerveren már nem tud megfogni,
   * mert ott a sorrend MÁR helyesnek látszik.
   */
  replace: (id: string, page: CapturedPage) => void;
  /** Új sorrend, azonosítók szerint. Az áttekintő hívja. */
  reorder: (ids: string[]) => void;
  /**
   * EGY MEGŐRZÖTT SOROZAT ÁTVÉTELE — a tárolóból vissza a képernyőre.
   *
   * A meglévő lapokat ELDOBJA (és visszavonja az URL-jeiket): a megőrzött
   * sorozat átvétele nem hozzáfűzés. Aki a tegnapi három lapot folytatja,
   * azt a hármat akarja látni, nem hármat plusz azt, amit közben fotózott —
   * és a felajánlás úgyis a kamera nyitásakor, üres sorozatra érkezik.
   */
  adopt: (pages: readonly CapturedPage[]) => void;
  clear: () => void;
}

export function useCaptureSession(): CaptureSession {
  const [pages, setPages] = useState<SessionPage[]>([]);

  // A LEBONTÁSKORI TAKARÍTÁSHOZ KELL A LEGFRISSEBB LISTA. Egy `useEffect`
  // üres függőséggel a MOUNT-kori (üres) listát látná, és egyetlen URL-t sem
  // vonna vissza — a szivárgás pont azon az úton maradna meg, amit védeni
  // akarunk.
  const latest = useRef<SessionPage[]>([]);
  latest.current = pages;

  useEffect(() => {
    return () => {
      for (const page of latest.current) {
        URL.revokeObjectURL(page.previewUrl);
      }
    };
  }, []);

  /**
   * A KÖVETKEZŐ AZONOSÍTÓ — monoton számláló, nem a lista hossza.
   *
   * ⚠️ A `pages.length` NEM ütközésmentes: három lapból a másodikat törölve
   * a következő felvétel megint a 2-es utótagot kapná, és az azonos azonosító
   * miatt a `remove`/`replace` a MÁSIK lapra ütne. `crypto.randomUUID`
   * helyett azért számláló, mert az csak biztonságos eredeten létezik, és egy
   * azonosító kedvéért nem akarunk második ágat a nem biztonságos esetre.
   */
  const nextId = useRef(0);
  const mintPage = useCallback((captured: CapturedPage): SessionPage => {
    nextId.current += 1;
    return {
      id: `lap-${nextId.current}`,
      blob: captured.blob,
      previewUrl: URL.createObjectURL(captured.blob),
      cropped: captured.cropped,
      original: captured.original,
      corners: captured.corners,
    };
  }, []);

  const add = useCallback(
    (captured: CapturedPage) => {
      // ⚠️ AZ URL A FRISSÍTŐN KÍVÜL KÉSZÜL. A `StrictMode` fejlesztésben
      // KÉTSZER futtatja a `setPages` frissítőjét, és a második eredményét
      // eldobja — egy frissítőn belüli `createObjectURL` tehát két URL-t
      // hozna létre, amiből az egyikre soha senki nem mutat, és így soha nem
      // is vonódik vissza. Pont abban a fájlban, aminek az EGYETLEN dolga,
      // hogy ne szivárogjon.
      const page = mintPage(captured);
      setPages((current) => [...current, page]);
    },
    [mintPage]
  );

  const remove = useCallback((id: string) => {
    // A VISSZAVONÁS IS A FRISSÍTŐN KÍVÜL, ugyanazért: a `StrictMode` alatt
    // kétszer futna le. A `latest` ref a kirajzolt listát tartja, tehát
    // ugyanazt látja, amit a felhasználó megnyomott.
    const removed = latest.current.find((page) => page.id === id);
    if (removed) {
      // AZONNAL, nem a következő takarításkor: egy törölt lap bájtjai
      // semmire nem kellenek, és a telefon memóriája véges.
      URL.revokeObjectURL(removed.previewUrl);
    }
    setPages((current) => current.filter((page) => page.id !== id));
  }, []);

  const replace = useCallback(
    (id: string, captured: CapturedPage) => {
      // ÚJ AZONOSÍTÓ, mert ez már egy másik kép. A régit megtartva egy `key`
      // szerinti újrarajzolás elmaradhatna, és a felhasználó a RÉGI
      // bélyegképet látná egy olyan lapon, amit épp újrafotózott.
      const page = mintPage(captured);

      const replaced = latest.current.find((current) => current.id === id);
      if (replaced) {
        URL.revokeObjectURL(replaced.previewUrl);
      }

      setPages((current) => {
        const index = current.findIndex((item) => item.id === id);
        if (index === -1) {
          // A LAP KÖZBEN ELTŰNT — nem dobunk el egy friss fotót. Ez akkor
          // fordulhat elő, ha az újrafotózás közben valami mást törölt; a
          // munkája akkor is a végére kerül, mint egy sima exponálás.
          return [...current, page];
        }
        const next = [...current];
        next[index] = page;
        return next;
      });
    },
    [mintPage]
  );

  const reorder = useCallback((ids: string[]) => {
    setPages((current) => {
      const byId = new Map(current.map((page) => [page.id, page]));
      const next: SessionPage[] = [];
      for (const id of ids) {
        const page = byId.get(id);
        if (page) {
          next.push(page);
          byId.delete(id);
        }
      }
      // ⚠️ AMI KIMARADT A SORRENDBŐL, AZ A VÉGÉRE KERÜL, NEM VÉSZ EL. A hívó
      // adhat hiányos listát — egy elszállt húzás, egy közben törölt lap —, és
      // egy néma eldobás itt a felhasználó MUNKÁJÁT dobná el: a lefotózott
      // oldal eltűnne a csíkról, magyarázat nélkül.
      return [...next, ...byId.values()];
    });
  }, []);

  const adopt = useCallback(
    (taken: readonly CapturedPage[]) => {
      for (const page of latest.current) {
        URL.revokeObjectURL(page.previewUrl);
      }
      setPages(taken.map(mintPage));
    },
    [mintPage]
  );

  const clear = useCallback(() => {
    for (const page of latest.current) {
      URL.revokeObjectURL(page.previewUrl);
    }
    setPages([]);
  }, []);

  // ⚠️ `useMemo`, NEM friss objektum minden kirajzoláskor. A `CameraSheet`
  // `capture`-je ettől a szótól függ, a detektáló `useEffect` pedig a
  // `capture`-től — egy új azonosság tehát a 125 ms-os időzítőt bontaná le és
  // építené újra, akár nyolcszor másodpercenként, és minden újraépítés egy
  // teljes körrel tolja ki a következő detektálást.
  return useMemo(
    () => ({ pages, add, remove, replace, reorder, adopt, clear }),
    [pages, add, remove, replace, reorder, adopt, clear]
  );
}
